import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const PressReleasesPage = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const { releaseId } = useParams();
  const [selectedRelease, setSelectedRelease] = useState(null);

  useEffect(() => {
    fetchReleases();
  }, []);

  useEffect(() => {
    if (releaseId) {
      fetchSingleRelease(releaseId);
    } else {
      setSelectedRelease(null);
    }
  }, [releaseId]);

  const fetchReleases = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/press-releases/published`);
      const data = await res.json();
      setReleases(data.press_releases || []);
    } catch (err) {
      console.error('Failed to fetch press releases:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleRelease = async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/press-releases/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedRelease(data);
      }
    } catch (err) {
      console.error('Failed to fetch press release:', err);
    }
  };

  // Single Release View
  if (selectedRelease) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>{selectedRelease.title} | Fort Worth Dental Press Release</title>
          <meta name="description" content={selectedRelease.excerpt || selectedRelease.title} />
        </Helmet>
        
        {/* Hero */}
        <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
          <div className="container mx-auto px-4">
            <Link to="/press-releases" className="text-teal-200 hover:text-white mb-4 inline-block">
              ← Back to All Press Releases
            </Link>
            <h1 className="text-4xl font-bold">{selectedRelease.title}</h1>
            <p className="mt-4 text-teal-100">
              Published: {new Date(selectedRelease.published_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="prose prose-lg max-w-none">
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {selectedRelease.content}
                </div>
              </div>

              {/* Keywords */}
              {selectedRelease.keywords && selectedRelease.keywords.length > 0 && (
                <div className="mt-8 pt-8 border-t">
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">Related Topics:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedRelease.keywords.map((kw, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-8 border-t">
                <h4 className="text-sm font-semibold text-gray-600 mb-2">Share this release:</h4>
                <div className="flex gap-3">
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(selectedRelease.title)}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500"
                  >
                    Share on Twitter
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800"
                  >
                    Share on LinkedIn
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Share on Facebook
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Box */}
            <div className="bg-teal-50 rounded-xl p-6 mt-8">
              <h4 className="font-semibold text-teal-800 mb-2">Media Contact</h4>
              <p className="text-teal-700">
                Fort Worth Dental<br />
                4620 Citylake Blvd W, Fort Worth, TX 76132<br />
                Phone: (817) 263-0202<br />
                Website: <a href="https://fortworthdental.com" className="underline">fortworthdental.com</a>
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Press Releases | Fort Worth Dental News</title>
        <meta name="description" content="Read the latest press releases and news from Fort Worth Dental. Stay informed about our services, community involvement, and dental industry updates." />
      </Helmet>
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Press Releases</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Latest news and announcements from Fort Worth Dental
          </p>
        </div>
      </section>

      {/* Press Releases List */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {loading ? (
            <div className="text-center py-12">Loading press releases...</div>
          ) : releases.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow">
              <div className="text-4xl mb-4">📰</div>
              <h3 className="text-xl font-semibold text-gray-700">No Press Releases Yet</h3>
              <p className="text-gray-500 mt-2">Check back soon for the latest news from Fort Worth Dental.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {releases.map((release) => (
                <article key={release.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <Link to={`/press-releases/${release.id}`} className="block p-6">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-800 hover:text-teal-600 transition-colors">
                          {release.title}
                        </h2>
                        <p className="text-gray-600 mt-3 line-clamp-2">
                          {release.excerpt || release.content?.substring(0, 200) + '...'}
                        </p>
                        <div className="flex items-center gap-4 mt-4">
                          <span className="text-sm text-gray-500">
                            {new Date(release.published_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                          {release.keywords && release.keywords.length > 0 && (
                            <div className="flex gap-2">
                              {release.keywords.slice(0, 2).map((kw, i) => (
                                <span key={i} className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-teal-600 font-semibold whitespace-nowrap">
                        Read More →
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Media Inquiries</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            For media inquiries, interview requests, or additional information about Fort Worth Dental, 
            please contact our team.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="tel:8172630202"
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
            >
              Call (817) 263-0202
            </a>
            <Link
              to="/contact-us"
              className="px-6 py-3 border border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 transition"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressReleasesPage;
